import { templateAtVersion } from '@bmo/form-core';
import { Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { type WorkflowTemplate, type WorkflowVersion, workflowsApi } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { PageHeader } from '@/components/PageHeader';
import { StatusTag } from '@/components/StatusTag';
import { handleApiError } from '@/lib/handleApiError';
import { assigneeLabel } from '@/utils/workflowHelpers';

import { type PreviewBase, WorkflowReviewBody } from './WorkflowReviewBody';

const { Text } = Typography;

/**
 * Read-only viewer for a SPECIFIC workflow version's details + step
 * configuration. Reached from Version History ("View details" on any row).
 * Unlike the runtime Preview (advisor form) this shows the authoring view —
 * the same read-only body the Approver sees during review — but rendered
 * entirely from the version's FROZEN snapshot so an archived version never
 * leaks the template's current state.
 *
 * Role-agnostic: the same component serves both the Originator and Approver
 * routes. Role only decides the Back destination and which /preview route the
 * nested per-step preview buttons target.
 */
export function VersionDetailsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id, v } = useParams<{ id: string; v: string }>();
  const { role } = useRoles();
  const isApprover = role === 'Approver';
  const roleBase: PreviewBase = isApprover ? '/approver/workflows' : '/originator/workflows';

  const requestedVersion = v !== undefined ? Number(v) : NaN;
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [version, setVersion] = useState<WorkflowVersion | null>(null);
  const [loading, setLoading] = useState(true);
  // Set when :v can't be resolved to a version row — we show an explicit error
  // instead of falling back to some other version's (or the current) config.
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        // A non-numeric :v can never match a row — flag it as not-found without
        // even hitting the versions endpoint.
        if (!Number.isFinite(requestedVersion)) {
          setNotFound(true);
          return;
        }
        // getVersion, not listVersions: this page renders the version's frozen step
        // graph, and the list endpoint no longer carries snapshots. A missing version
        // 404s, which is the not-found case we already handle.
        const [fresh, pinned] = await Promise.all([
          workflowsApi.get(id),
          workflowsApi.getVersion(id, requestedVersion).catch(() => null),
        ]);
        if (cancelled) return;
        if (!pinned) {
          setNotFound(true);
          return;
        }
        setTemplate(fresh);
        setVersion(pinned);
      } catch (err) {
        if (!cancelled) handleApiError(err, t, t('workflow.versions.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t, requestedVersion]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (notFound || !template || !version) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="secondary">{t('workflow.versions.detailsNotFound')}</Text>
      </div>
    );
  }

  const isFr = i18n.language.startsWith('fr');
  const name = isFr ? version.nameFr : version.nameEn;
  const description = isFr ? version.descriptionFr : version.descriptionEn;
  // This row IS the staged version (approved, awaiting the Approver's publish
  // toggle) rather than one truly superseded by a newer live version — those
  // are both "Approved + not active" from the row's own perspective, but only
  // the latter is "Archived". See StatusTag's hasPendingApproval doc.
  const isPendingPublish = template.pendingApprovedVersionNumber === version.versionNumber;
  // Distinguishes "current version, manually unpublished" (Unpublished) from
  // a version truly superseded by a newer one (Archived). See StatusTag's
  // `isCurrentVersion` doc.
  const isCurrentVersion = template.currentVersionNumber === version.versionNumber;
  // This version's OWN frozen dealer-code lists when it carries them. A row predating the
  // per-version freeze (2026-10-26) reports null — NOT empty — and falls back to the
  // template's live lists, which is the only thing it can honestly show. `live` is what
  // captions that fallback; without it the page would present today's gating as history.
  const dealerCodes = {
    included: version.includedDealerCodes ?? template.includedDealerCodes,
    excluded: version.excludedDealerCodes ?? template.excludedDealerCodes,
    live: version.includedDealerCodes == null,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title={version.nameEn || template.appName}
        titleSuffix={
          <StatusTag
            status={version.status}
            isActive={version.isActive}
            scope="version"
            hasPendingApproval={isPendingPublish}
            isCurrentVersion={isCurrentVersion}
          />
        }
        // Browser-back, not a hardcoded route: this page is reached from several
        // places (Version History, the Approver dashboard's Pending
        // Publishing/Approved tabs, ...) and hardcoding one of them stranded
        // everyone else on a detour through Version History instead of back
        // where they actually came from.
        onBack={() => navigate(-1)}
      />

      {/* Everything below is fed from the version's frozen snapshot — including
          the steps passed as `template` so condition-label resolution stays
          consistent with what this version actually contained. */}
      <WorkflowReviewBody
        isFr={isFr}
        name={name}
        description={description}
        assignee={assigneeLabel(version.type, t)}
        platform={version.platform}
        versionLabel={version.versionLabel}
        status={version.status}
        isActive={version.isActive}
        statusScope="version"
        hasPendingApproval={isPendingPublish}
        isCurrentVersion={isCurrentVersion}
        rejectionComment={version.rejectionComment}
        steps={version.steps ?? []}
        template={templateAtVersion(template, version)}
        versionNumber={version.versionNumber}
        previewBase={roleBase}
        includedDealerCodes={dealerCodes.included}
        excludedDealerCodes={dealerCodes.excluded}
        dealerCodesAreLive={dealerCodes.live}
        packageFilenameTemplate={version.packageFilenameTemplate}
        dateFormat={version.dateFormat}
        isFrozenSnapshot
      />
    </div>
  );
}
